import React from 'react';
import styled from 'styled-components';

import { Image } from 'UI-Dash-Commons-UI';
import BottomLeftMenuComponent from './BottomLeftMenuComponent';
import LeftSideMenuSectionComponent from './LeftSideMenuSectionComponent';
import MenuSpec from "../../../types/MenuSpec";
import MenuSection from "../../../types/MenuSection";

const collapsedLogo = require( '../images/menu_collapsed_logo.svg' );
const fullLogo = require( '../images/menu_full_logo.svg' );

type LeftSideMenuComponentProps =
    {
        bottomLeftMenuSpec: MenuSpec | null,
        isOpen: boolean,
        isPinnedOpen: boolean,
        leftSideMenuSpec: MenuSpec | null,
        onSectionActivated: ( sectionSpec: MenuSection ) => void,
        onSectionActivatedIframeWindowLocation: ( currentSectionSpec: MenuSection, newSectionSpec: MenuSection ) => void,
        onUpdateMenuOpenState: ( isOpen: boolean ) => void,
        onUpdateOpenSectionId: ( id: string | null ) => void,
        openSectionId: string
    };

const LeftSideMenuComponent: React.StatelessComponent<LeftSideMenuComponentProps> = ( {
                                                                                          isOpen,
                                                                                          isPinnedOpen,
                                                                                          openSectionId,
                                                                                          leftSideMenuSpec,
                                                                                          bottomLeftMenuSpec,
                                                                                          onUpdateMenuOpenState,
                                                                                          onUpdateOpenSectionId,
                                                                                          onSectionActivated,
                                                                                          onSectionActivatedIframeWindowLocation
                                                                                      } ) =>
{

    // If there is no menu
    if ( !leftSideMenuSpec )
    {
        // Just return an empty wrapper
        return <MenuWrapper/>;
    }

    const level = 0;

    const leftSideMenuRootSpec: MenuSection = new MenuSection( { children: leftSideMenuSpec.menu } );

    const openMenu: () => void = () =>
    {
        onUpdateMenuOpenState( true );
    };

    const maybeCloseMenu: () => void = () =>
    {
        onUpdateMenuOpenState( isPinnedOpen );
    };

    /**
     * This is the workaround for the bug on mobile Chrome where tapping on left menu after navigation is broken.
     *
     * Seems like this happens because at the time of navigation, UI forcefully close the the left nav menu
     * without triggering it via natural 'mouseleave' event (See MainNavigations > actions > activateLeftMenuSection)
     * and when user touches the nav bar again, 'mouseenter' event is not triggered since user hasn't touched
     * outside the left nav bar.
     *
     * Since subsequent tapping on the menu is captured via click event at LeftSideMenuSectionComponent
     * via onUpdateOpenSectionId, opening the menu there at onUpdateOpenSectionId resolves this issue.
     */
    const updateOpenSectionIdAndMaybeOpenMenu: ( sectionId: string ) => void = ( sectionId: string ) =>
    {
        if ( !isOpen )
        {
            onUpdateMenuOpenState( true );
        }

        onUpdateOpenSectionId( sectionId );
    };

    return <MenuWrapper
        isOpen={isOpen}
        isPinnedOpen={isPinnedOpen}
        onMouseEnter={openMenu}
        onMouseLeave={maybeCloseMenu}
    >
        <Logo
            src={isOpen ? fullLogo : collapsedLogo}
            alt={'AppLovin'}
            height={'24px'}
        />

        <LeftSideMenuSectionComponent
            level={level}
            isLeftSideMenuOpen={isOpen}
            sectionSpec={leftSideMenuRootSpec}
            activeSectionSpec={leftSideMenuSpec.activeSection}

            openSectionId={openSectionId}
            onUpdateOpenSectionId={updateOpenSectionIdAndMaybeOpenMenu}
            onSectionActivated={onSectionActivated}
            onSectionActivatedIframeWindowLocation={onSectionActivatedIframeWindowLocation}
        />
        <BottomLeftMenuComponent
            isLeftSideMenuOpen={isOpen}
            isLeftSideMenuPinnedOpen={isPinnedOpen}
            bottomLeftMenuSpec={bottomLeftMenuSpec}
            onSectionActivated={onSectionActivated}
        />
    </MenuWrapper>;
};

const MenuWrapper = styled<{ isOpen?: boolean, isPinnedOpen?: boolean }, 'nav'>( 'nav' )`
  
  width: 255px;
  width: ${props => !props.isOpen && '74px'};

  min-height: 100vh;
  max-height: 100vh;

  position: fixed;
  position: ${props => props.isPinnedOpen && 'inherit'};

  display: flex;
  flex-direction: column;
  z-index: 10;

  transition-delay: 0.15s;
  transition: width 0.33s linear;
  
  box-shadow: -3px 0 5px 0;

  overflow: hidden;

  background-color: ${props => props.theme.colors.navTertiary};
`;

const Logo = styled( Image )`
  padding: 6px 0 6px 10px;

  background-color: ${props => props.theme.colors.navPrimary};  
`;

export default LeftSideMenuComponent;

